import React, { useEffect, useState, useMemo } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Loader2, Search, Download, AlertTriangle, ShieldAlert, MapPin,
  Wallet, FileWarning, Clock, HelpCircle, CheckCircle, Eye, RefreshCw, MessageSquare,
  RotateCcw, Gavel
} from 'lucide-react';
import { toast } from 'sonner';

const CAT_ICONS = {
  auth: ShieldAlert, not_found: MapPin, insufficient_balance: Wallet,
  name_validation: FileWarning, tsq_timeout: Clock, webhook: RefreshCw,
  tier_limit: AlertTriangle, mfb_error: AlertTriangle, unknown: HelpCircle,
};
const CAT_COLORS = {
  auth: 'red', not_found: 'orange', insufficient_balance: 'amber',
  name_validation: 'purple', tsq_timeout: 'blue', webhook: 'pink',
  tier_limit: 'red', mfb_error: 'orange', unknown: 'gray',
};

export default function TransactionErrors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState(null);
  const [resolveModal, setResolveModal] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [detailModal, setDetailModal] = useState(null);
  const [webhookData, setWebhookData] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  // Manual correction state
  const [reverseModal, setReverseModal] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseOtp, setReverseOtp] = useState('');
  const [reverseOtpSent, setReverseOtpSent] = useState(false);
  const [reverseConfirmRef, setReverseConfirmRef] = useState('');
  const [resolveManualModal, setResolveManualModal] = useState(null);
  const [resolveManualReason, setResolveManualReason] = useState('');
  const [resolveManualType, setResolveManualType] = useState('complete');
  const [resolveManualMfbRef, setResolveManualMfbRef] = useState('');
  // Timeline state
  const [detailTab, setDetailTab] = useState('info');
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const loadTimeline = async (reference) => {
    if (!reference) return;
    setTimelineLoading(true);
    try {
      const data = await superAdminAPI.getTransactionTimeline(reference);
      setTimelineData(data);
    } catch (err) {
      console.error('Timeline load error', err);
      setTimelineData(null);
    } finally {
      setTimelineLoading(false);
    }
  };

  const load = async (status = 'active') => {
    try { setData(await superAdminAPI.getTransactionErrors({ status })); }
    catch { toast.error('Failed to load errors'); }
    finally { setLoading(false); }
  };

  const loadWebhooks = async () => {
    try { setWebhookData(await superAdminAPI.getWebhookErrors()); } catch {}
  };

  useEffect(() => { load(tab === 'resolved' ? 'resolved' : 'active'); }, [tab]);
  useEffect(() => { if (tab === 'webhooks') loadWebhooks(); }, [tab]);

  const handleResolve = async () => {
    if (resolveNote.length < 5) { toast.error('Note must be at least 5 characters'); return; }
    setActionLoading(resolveModal);
    try {
      await superAdminAPI.resolveTransactionError(resolveModal, resolveNote);
      toast.success('Error resolved');
      setResolveModal(null); setResolveNote('');
      load('active');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleSendReversalOtp = async () => {
    if (!reverseModal) return;
    setActionLoading('reverse-otp');
    try {
      await superAdminAPI.sendReversalOtp(reverseModal.reference);
      setReverseOtpSent(true);
      toast.success('OTP sent to your email');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send OTP'); }
    finally { setActionLoading(null); }
  };

  const handleReverse = async () => {
    if (!reverseModal) return;
    if (reverseReason.length < 10) { toast.error('Reason must be at least 10 characters'); return; }
    if (!reverseOtp) { toast.error('Enter the OTP code'); return; }
    if (reverseConfirmRef !== reverseModal.reference) { toast.error('Reference does not match'); return; }
    setActionLoading('reverse');
    try {
      const res = await superAdminAPI.reverseTransaction(reverseModal.reference, {
        reason: reverseReason, otp_code: reverseOtp,
      });
      toast.success(`Reversed! New balance: NGN ${Number(res.wallet_new_balance).toLocaleString()}`);
      setReverseModal(null); setReverseReason(''); setReverseOtp(''); setReverseOtpSent(false); setReverseConfirmRef('');
      load(tab === 'resolved' ? 'resolved' : 'active');
    } catch (err) { toast.error(err.response?.data?.detail || 'Reversal failed'); }
    finally { setActionLoading(null); }
  };

  const handleManualResolve = async () => {
    if (!resolveManualModal) return;
    if (resolveManualReason.length < 10) { toast.error('Reason must be at least 10 characters'); return; }
    setActionLoading('manual-resolve');
    try {
      const res = await superAdminAPI.resolveManualTransaction(resolveManualModal.reference, {
        resolution: resolveManualType, reason: resolveManualReason,
        mfb_confirmation_reference: resolveManualMfbRef || undefined,
      });
      toast.success(res.message || 'Resolved');
      setResolveManualModal(null); setResolveManualReason(''); setResolveManualType('complete'); setResolveManualMfbRef('');
      load(tab === 'resolved' ? 'resolved' : 'active');
    } catch (err) { toast.error(err.response?.data?.detail || 'Resolution failed'); }
    finally { setActionLoading(null); }
  };

  const filtered = useMemo(() => {
    let items = data?.transactions || [];
    if (search) items = items.filter(t =>
      t.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      t.reference?.toLowerCase().includes(search.toLowerCase())
    );
    if (catFilter) items = items.filter(t => t.error_category === catFilter);
    return items;
  }, [data, search, catFilter]);

  const exportCSV = () => {
    if (!filtered.length) return;
    const headers = ['Reference', 'Company', 'Employee', 'Type', 'Amount', 'Status', 'Error', 'Category', 'Retries', 'Date'];
    const rows = filtered.map(t => [t.reference, t.company_name, t.employee_name || '', t.type, t.amount, t.status, t.failure_reason || '', t.error_category_label, t.retry_count, t.created_at]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `transaction-errors-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); toast.success('CSV exported');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const cats = data?.summary?.category_counts || {};

  return (
    <div className="space-y-6" data-testid="transaction-errors-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Transaction Errors</h1>
          <p className="text-sm text-gray-500">Monitor and resolve failed transactions across all companies</p>
        </div>
        <Button variant="outline" onClick={exportCSV} className="h-9 text-xs" data-testid="export-errors-csv">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Category Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {Object.entries(CAT_ICONS).map(([key, Icon]) => {
          const color = CAT_COLORS[key];
          const count = cats[key] || 0;
          const isActive = catFilter === key;
          return (
            <button key={key} onClick={() => setCatFilter(isActive ? null : key)}
              className={`p-3 rounded-xl border transition-all text-left ${isActive ? `border-${color}-300 bg-${color}-50 shadow-sm` : 'border-gray-200 hover:border-gray-300'}`}
              data-testid={`cat-${key}`}>
              <Icon className={`w-4 h-4 text-${color}-500 mb-1`} />
              <p className="text-lg font-bold text-gray-900">{count}</p>
              <p className="text-[10px] text-gray-500 leading-tight">{key.replace('_', ' ')}</p>
            </button>
          );
        })}
      </div>

      {/* Tabs + Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
          {[{ id: 'active', label: 'Active' }, { id: 'awaiting', label: 'Awaiting Confirmation' }, { id: 'resolved', label: 'Resolved' }, { id: 'webhooks', label: 'Webhooks' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`error-tab-${t.id}`}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
              {t.id === 'awaiting' && (data?.summary?.awaiting_confirmation_count || 0) > 0 && (
                <span className="ml-1 text-[10px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full">
                  {data.summary.awaiting_confirmation_count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            className="h-9 pl-9 text-sm" data-testid="search-errors" />
        </div>
        {data?.summary?.unresolved_count > 0 && (
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-600" data-testid="unresolved-badge">
            {data.summary.unresolved_count} unresolved
          </span>
        )}
      </div>

      {/* Transaction Errors */}
      {(tab === 'active' || tab === 'resolved') && (
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <Card className="border-gray-200"><CardContent className="p-12 text-center">
              <CheckCircle className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{tab === 'resolved' ? 'No resolved errors' : 'No active errors'}</p>
            </CardContent></Card>
          ) : filtered.map(t => (
            <Card key={t.id} className="border-gray-200" data-testid={`error-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-gray-400">{t.reference}</span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full bg-${CAT_COLORS[t.error_category]}-50 text-${CAT_COLORS[t.error_category]}-600`}>
                        {t.error_category_label}
                      </span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        t.status === 'resolved' ? 'bg-emerald-50 text-emerald-600' :
                        t.status === 'review_required' ? 'bg-amber-50 text-amber-600' :
                        'bg-red-50 text-red-600'
                      }`}>{t.status}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">{t.company_name} {t.employee_name ? `\u2014 ${t.employee_name}` : ''}</p>
                    <p className="text-xs text-red-500 mt-1 line-clamp-2">{t.failure_reason || 'No error message'}</p>
                    {t.resolution_note && <p className="text-xs text-emerald-600 mt-1">Resolved: {t.resolution_note}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-gray-900">NGN {Number(t.amount).toLocaleString()}</p>
                    <p className="text-[10px] text-gray-400">{t.type}</p>
                    <p className="text-[10px] text-gray-400">{new Date(t.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    {t.retry_count > 0 && <p className="text-[10px] text-amber-500">{t.retry_count} retries</p>}
                  </div>
                </div>
                {tab === 'active' && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setDetailModal(t); setDetailTab('info'); setTimelineData(null); }} data-testid={`view-${t.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Detail
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => { setResolveModal(t.id); setResolveNote(''); }} data-testid={`resolve-${t.id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Resolve
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                      onClick={() => { setReverseModal(t); setReverseReason(''); setReverseOtp(''); setReverseOtpSent(false); setReverseConfirmRef(''); }}
                      data-testid={`reverse-btn-${t.id}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Reverse
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Awaiting Confirmation Tab */}
      {tab === 'awaiting' && (
        <div className="space-y-2" data-testid="awaiting-confirmation-section">
          {(!data?.awaiting_confirmation || data.awaiting_confirmation.length === 0) ? (
            <Card className="border-gray-200"><CardContent className="p-12 text-center">
              <CheckCircle className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No transactions awaiting confirmation</p>
            </CardContent></Card>
          ) : data.awaiting_confirmation.map(t => (
            <Card key={t.id} className="border-amber-200 bg-amber-50/30" data-testid={`awaiting-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-gray-400">{t.reference}</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" /> Awaiting Confirmation
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">{t.company_name} {t.employee_name ? `\u2014 ${t.employee_name}` : ''}</p>
                    <p className="text-xs text-amber-600 mt-1">Processing since {t.mfb_initiated_at ? new Date(t.mfb_initiated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'unknown'}</p>
                    {t.payout_account_name && <p className="text-xs text-gray-500 mt-0.5">To: {t.payout_account_name} ({t.payout_bank_name})</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-gray-900">NGN {Number(t.amount).toLocaleString()}</p>
                    <p className="text-[10px] text-gray-400">{t.type}</p>
                    <p className="text-[10px] text-gray-400">{new Date(t.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-amber-200">
                  <Button variant="outline" size="sm" className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                    onClick={() => { setResolveManualModal(t); setResolveManualReason(''); setResolveManualType('complete'); setResolveManualMfbRef(''); }}
                    data-testid={`resolve-awaiting-${t.id}`}>
                    <Gavel className="w-3 h-3 mr-1" /> Resolve
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Webhook Errors Tab */}
      {tab === 'webhooks' && (
        <div className="space-y-2">
          {!webhookData || webhookData.events?.length === 0 ? (
            <Card className="border-gray-200"><CardContent className="p-12 text-center">
              <CheckCircle className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No webhook errors</p>
            </CardContent></Card>
          ) : webhookData.events.map(e => (
            <Card key={e.id} className="border-gray-200" data-testid={`webhook-error-${e.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-400">{e.external_reference || e.id.slice(0,8)}</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-pink-50 text-pink-600">{e.event_type}</span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${e.is_verified ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                        {e.is_verified ? 'Verified' : 'Unverified'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 font-medium">Status: {e.status}</p>
                    {e.last_error && <p className="text-xs text-red-500 mt-1">{e.last_error}</p>}
                    <p className="text-[10px] text-gray-400 mt-1">Attempts: {e.processing_attempts}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-gray-400">{new Date(e.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                <details className="mt-2">
                  <summary className="text-[10px] text-gray-400 cursor-pointer hover:text-gray-600">View raw payload</summary>
                  <pre className="mt-2 p-2 bg-gray-50 rounded text-[10px] text-gray-600 overflow-x-auto max-h-40" data-testid={`webhook-payload-${e.id}`}>
                    {JSON.stringify(e.payload, null, 2)}
                  </pre>
                </details>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal with Timeline */}
      <Dialog open={!!detailModal} onOpenChange={() => { setDetailModal(null); setTimelineData(null); }}>
        {detailModal && (
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-base">Transaction Detail</DialogTitle></DialogHeader>
            {/* Tab toggle */}
            <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg mb-3">
              <button onClick={() => setDetailTab('info')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${detailTab === 'info' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`} data-testid="detail-tab-info">Info</button>
              <button onClick={() => { setDetailTab('timeline'); loadTimeline(detailModal.reference); }} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${detailTab === 'timeline' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`} data-testid="detail-tab-timeline">Timeline</button>
            </div>
            {detailTab === 'info' ? (
              <div className="space-y-3 text-xs">
                {[
                  ['Reference', detailModal.reference],
                  ['External Ref', detailModal.external_reference],
                  ['Company', detailModal.company_name],
                  ['Employee', detailModal.employee_name],
                  ['Type', detailModal.type],
                  ['Amount', `NGN ${Number(detailModal.amount).toLocaleString()}`],
                  ['Status', detailModal.status],
                  ['Bank', detailModal.payout_bank_name],
                  ['Account', detailModal.payout_account_number],
                  ['Retries', detailModal.retry_count],
                  ['Date', detailModal.created_at],
                ].map(([label, val]) => val ? (
                  <div key={label} className="flex justify-between border-b border-gray-100 pb-1">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-medium text-gray-900 text-right max-w-[60%]">{String(val)}</span>
                  </div>
                ) : null)}
                <div>
                  <p className="text-gray-500 mb-1">Error Message</p>
                  <p className="text-red-500 bg-red-50 p-2 rounded">{detailModal.failure_reason || 'No error message'}</p>
                </div>
                {detailModal.status !== 'reversed' && (
                  <Button variant="outline" size="sm" className="w-full h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50 mt-2"
                    onClick={() => { setDetailModal(null); setReverseModal(detailModal); setReverseReason(''); setReverseOtp(''); setReverseOtpSent(false); setReverseConfirmRef(''); }}
                    data-testid="detail-reverse-btn">
                    <RotateCcw className="w-3 h-3 mr-1" /> Reverse This Transaction
                  </Button>
                )}
                {detailModal.status === 'reversed' && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 mt-2">
                    <RotateCcw className="w-3 h-3" /> Transaction Reversed
                  </span>
                )}
              </div>
            ) : (
              <div data-testid="timeline-view">
                {timelineLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                ) : timelineData ? (
                  <div className="space-y-4">
                    {/* Duration header */}
                    <div className="text-center py-2 px-3 rounded-lg bg-gray-50 border border-gray-200">
                      <p className="text-xs text-gray-500">{timelineData.status === 'processing' ? 'Processing for' : timelineData.status === 'completed' ? 'Completed in' : timelineData.status === 'reversed' ? 'Reversed after' : 'Duration'}</p>
                      <p className="text-sm font-bold text-gray-900">{timelineData.total_duration_label}</p>
                    </div>
                    {/* Warnings */}
                    {timelineData.warnings?.length > 0 && (
                      <div className="space-y-1.5">
                        {timelineData.warnings.map((w, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-amber-700">{w}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Timeline nodes */}
                    <div className="relative pl-6">
                      {/* Vertical line */}
                      <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-gray-200" />
                      {timelineData.timeline.map((ev, i) => {
                        const isLast = i === timelineData.timeline.length - 1;
                        const nodeColor = ev.event === 'waiting' ? 'bg-amber-400' : ev.event === 'failed' ? 'bg-red-500' : ev.event === 'reversed' ? 'bg-amber-500' : 'bg-emerald-500';
                        const ringColor = ev.event === 'waiting' ? 'ring-amber-100' : ev.event === 'failed' ? 'ring-red-100' : ev.event === 'reversed' ? 'ring-amber-100' : 'ring-emerald-100';
                        return (
                          <div key={i} className={`relative pb-5 ${isLast ? 'pb-0' : ''}`} data-testid={`timeline-event-${ev.event}`}>
                            {/* Node dot */}
                            <div className={`absolute -left-6 top-0.5 w-[18px] h-[18px] rounded-full ${nodeColor} ring-4 ${ringColor} z-10 flex items-center justify-center`}>
                              {ev.event === 'waiting' && <Loader2 className="w-2.5 h-2.5 text-white animate-spin" />}
                              {ev.event === 'completed' && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                              {(ev.event === 'failed' || ev.event === 'reversed') && <AlertTriangle className="w-2.5 h-2.5 text-white" />}
                            </div>
                            {/* Content */}
                            <div className="ml-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-xs font-semibold text-gray-900">{ev.label}</p>
                                {ev.duration_since_previous && <span className="text-[10px] text-gray-400 font-mono">+{ev.duration_since_previous}</span>}
                              </div>
                              {ev.timestamp && <p className="text-[10px] text-gray-400">{new Date(ev.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>}
                              <p className="text-[10px] text-gray-500 mt-0.5">{ev.note}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : <p className="text-center text-xs text-gray-400 py-8">Failed to load timeline</p>}
              </div>
            )}
          </DialogContent>
        )}
      </Dialog>

      {/* Resolve Modal */}
      <Dialog open={!!resolveModal} onOpenChange={() => setResolveModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">Resolve Error</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <textarea value={resolveNote} onChange={e => setResolveNote(e.target.value)}
              className="w-full h-24 px-3 py-2 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
              placeholder="Resolution note..." data-testid="resolve-note-input" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setResolveModal(null)}>Cancel</Button>
              <Button onClick={handleResolve} disabled={resolveNote.length < 5 || !!actionLoading}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="confirm-resolve-btn">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark Resolved'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reverse Transaction Modal */}
      <Dialog open={!!reverseModal} onOpenChange={() => { setReverseModal(null); setReverseOtpSent(false); }}>
        {reverseModal && (
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><RotateCcw className="w-4 h-4 text-amber-500" /> Reverse Transaction</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-700 font-medium">This will return NGN {Number(reverseModal.amount).toLocaleString()} + fees to the source wallet.</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Reason (min 10 chars)</label>
                <textarea value={reverseReason} onChange={e => setReverseReason(e.target.value)}
                  className="w-full h-20 px-3 py-2 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-amber-200"
                  placeholder="Why is this transaction being reversed?" data-testid="reverse-reason-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Type the reference to confirm</label>
                <Input value={reverseConfirmRef} onChange={e => setReverseConfirmRef(e.target.value)}
                  placeholder={reverseModal.reference} className="text-xs font-mono" data-testid="reverse-confirm-ref-input" />
                {reverseConfirmRef && reverseConfirmRef !== reverseModal.reference && (
                  <p className="text-[10px] text-red-500 mt-1">Reference does not match</p>
                )}
              </div>
              {!reverseOtpSent ? (
                <Button onClick={handleSendReversalOtp} disabled={reverseReason.length < 10 || reverseConfirmRef !== reverseModal.reference || actionLoading === 'reverse-otp'}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="send-reversal-otp-btn">
                  {actionLoading === 'reverse-otp' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Send OTP to Verify
                </Button>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Enter OTP code</label>
                    <Input value={reverseOtp} onChange={e => setReverseOtp(e.target.value)}
                      placeholder="6-digit code" className="text-center text-lg tracking-widest font-mono" maxLength={6}
                      data-testid="reverse-otp-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => { setReverseModal(null); setReverseOtpSent(false); }}>Cancel</Button>
                    <Button onClick={handleReverse} disabled={!reverseOtp || actionLoading === 'reverse'}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-reverse-btn">
                      {actionLoading === 'reverse' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      Confirm Reversal
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Manual Resolve Modal (for awaiting confirmation) */}
      <Dialog open={!!resolveManualModal} onOpenChange={() => setResolveManualModal(null)}>
        {resolveManualModal && (
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Gavel className="w-4 h-4 text-blue-500" /> Resolve Transaction</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-600"><strong>{resolveManualModal.reference}</strong> — NGN {Number(resolveManualModal.amount).toLocaleString()}</p>
                {resolveManualModal.payout_account_name && (
                  <p className="text-xs text-gray-500 mt-1">To: {resolveManualModal.payout_account_name} ({resolveManualModal.payout_bank_name})</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-2">Resolution</label>
                <div className="flex gap-2">
                  <button onClick={() => setResolveManualType('complete')} data-testid="resolve-type-complete"
                    className={`flex-1 p-3 rounded-lg border text-sm font-medium transition-all ${resolveManualType === 'complete' ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                    <CheckCircle className="w-4 h-4 mx-auto mb-1" />
                    Mark Completed
                  </button>
                  <button onClick={() => setResolveManualType('fail')} data-testid="resolve-type-fail"
                    className={`flex-1 p-3 rounded-lg border text-sm font-medium transition-all ${resolveManualType === 'fail' ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                    <RotateCcw className="w-4 h-4 mx-auto mb-1" />
                    Mark Failed (Refund)
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Reason (min 10 chars)</label>
                <textarea value={resolveManualReason} onChange={e => setResolveManualReason(e.target.value)}
                  className="w-full h-20 px-3 py-2 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Reason for this resolution..." data-testid="resolve-manual-reason-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">MFB Reference (optional)</label>
                <Input value={resolveManualMfbRef} onChange={e => setResolveManualMfbRef(e.target.value)}
                  placeholder="If MFB provided a confirmation ref" className="text-sm" data-testid="resolve-manual-mfb-ref-input" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setResolveManualModal(null)}>Cancel</Button>
                <Button onClick={handleManualResolve} disabled={resolveManualReason.length < 10 || actionLoading === 'manual-resolve'}
                  className={`flex-1 text-white ${resolveManualType === 'complete' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}`}
                  data-testid="confirm-manual-resolve-btn">
                  {actionLoading === 'manual-resolve' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {resolveManualType === 'complete' ? 'Confirm Completed' : 'Confirm Failed & Refund'}
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
